import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import MapView from "@/components/MapView";
import BookingModal from "@/components/BookingModal";
import ScannedBusinessBanner from "@/components/ScannedBusinessBanner";
import { Clock, MapPin, Filter, Crown } from "lucide-react";
import { todayStatus } from "@/lib/businessHours";
import { fmtSydneyDateTime } from "@/lib/timezone";
import { displayPrice } from "@/lib/price";

const SERVICE_FILTERS = [
  "All",
  "Hair",
  "Makeup",
  "Brows & Lashes",
  "Spray Tans",
  "Barber",
  "Skin and Injectables",
  "Nails",
  "Massage & Body",
  "Wellness",
];

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const LONG_HOUR_THRESHOLD = 8; // past 8h fall back to absolute datetime

function minutesUntil(dt) {
  const diff = (new Date(dt).getTime() - Date.now()) / MS_PER_MINUTE;
  if (diff < 0) return "started";
  if (diff < MINUTES_PER_HOUR) return `in ${Math.round(diff)}m`;
  const h = Math.floor(diff / MINUTES_PER_HOUR);
  const m = Math.round(diff % MINUTES_PER_HOUR);
  return h < LONG_HOUR_THRESHOLD ? `in ${h}h ${m}m` : fmtSydneyDateTime(dt);
}

// True once the opening's start_time is in the past — matches the backend
// guard so we don't offer a booking that will 400.
function hasStarted(dt) {
  return new Date(dt).getTime() <= Date.now();
}

export default function Landing() {
  const [openings, setOpenings] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // ?biz=<id> from a scanned QR code. When present, we highlight that
  // business + show a follow-us banner, but the rest of the map stays
  // fully explorable.
  const scannedBizId = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("biz");
  }, []);
  // `?h=<int>` optionally attaches a headline variant so the business can
  // see which printed poster line converts walk-ins best. See Marketing.jsx.
  const scanHeadlineIndex = useMemo(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("h");
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 && n <= 50 ? n : null;
  }, []);
  const [scannedBiz, setScannedBiz] = useState(null); // resolved business info

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/openings", { params: { status: "open" } });
      setOpenings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the scanned business (name + coords) so we can render the banner
  // and pan the map on first paint. Non-blocking — landing still loads openings.
  useEffect(() => {
    if (!scannedBizId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${scannedBizId}`);
        if (!cancelled) setScannedBiz(data);
      } catch (err) {
        // 404 or network — silently ignore, the URL is user-supplied.
        // debug-only so we still see it in dev if the QR link ever breaks.
        if (process.env.NODE_ENV !== "production") {
          console.debug("[Landing] scanned business lookup failed:", err?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [scannedBizId]);

  // Fire a single QR-scan ping per browser session so the business can see
  // which headline variant a walk-in scanned. Server dedupes on
  // (biz, headline, session) so reloads are safe.
  useEffect(() => {
    if (!scannedBizId || scanHeadlineIndex == null) return;
    if (typeof window === "undefined") return;
    const key = `ba_scan_${scannedBizId}_${scanHeadlineIndex}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
    } catch {
      // Private-mode / disabled storage — still ping, server-side dedupe
      // covers us if a session UUID happens to be reused.
    }
    let sid;
    try {
      sid = window.localStorage.getItem("ba_scan_sid");
      if (!sid) {
        sid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
        window.localStorage.setItem("ba_scan_sid", sid);
      }
    } catch {
      sid = String(Date.now()) + Math.random().toString(16).slice(2);
    }
    api.post("/scans", {
      business_id: scannedBizId,
      headline_index: scanHeadlineIndex,
      session_id: sid,
    }).catch(() => { /* fire-and-forget */ });
  }, [scannedBizId, scanHeadlineIndex]);

  const filtered = useMemo(() => {
    const base =
      filter === "All"
        ? openings
        : openings.filter((o) => o.service_type === filter);
    // Sort openings whose business is Open Now to the top of the list.
    // Stable within each bucket — we keep the server's `created_at` order
    // so the newest slots always float within their group.
    const now = new Date();
    const bucketed = base.map((o, idx) => {
      const s = todayStatus(o.business_hours, now);
      return { o, idx, isOpenNow: s.tone === "open" };
    });
    bucketed.sort((a, b) => {
      if (a.isOpenNow !== b.isOpenNow) return a.isOpenNow ? -1 : 1;
      return a.idx - b.idx;
    });
    return bucketed.map((x) => x.o);
  }, [openings, filter]);

  const openBooking = (o) => {
    setSelected(o);
    setModalOpen(true);
  };

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col md:flex-row">
      {/* MAP (60%) */}
      <div className="relative h-[50vh] w-full md:h-full md:w-3/5 border-b md:border-b-0 md:border-r border-border">
        <MapView
          openings={filtered}
          onSelect={(o) => {
            setSelected(o);
          }}
          selectedId={selected?.id}
          center={
            scannedBiz && typeof scannedBiz.lat === "number"
              ? [scannedBiz.lat, scannedBiz.lng]
              : undefined
          }
        />
        <div className="pointer-events-none absolute left-4 top-4 z-[1000]">
          <div className="pointer-events-auto bg-background/90 backdrop-blur-md border border-border px-3 py-2">
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
              live • sydney
            </p>
            <p className="text-sm font-semibold tracking-tight">
              {filtered.length} open right now
            </p>
          </div>
        </div>
        {selected && (
          <div className="pointer-events-auto absolute bottom-4 left-4 right-4 z-[1000] md:right-auto md:max-w-sm">
            <div className="border border-foreground bg-background p-4 shadow-2xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    {selected.service_type}
                  </p>
                  <h3 className="mt-1 text-lg leading-tight tracking-tight">
                    {selected.title}
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {selected.business_name}
                  </p>
                </div>
                <button
                  data-testid="close-map-card-btn"
                  onClick={() => setSelected(null)}
                  className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-[11px]">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock size={12} />
                  <span>{minutesUntil(selected.start_time)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground truncate">
                  <MapPin size={12} />
                  <span className="truncate">{selected.address}</span>
                </div>
              </div>
              <button
                data-testid="map-card-book-btn"
                onClick={() => openBooking(selected)}
                disabled={hasStarted(selected.start_time)}
                className="mt-3 w-full gold-metallic-bg px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
              >
                {hasStarted(selected.start_time) ? "Bookings closed" : "Request to Book"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* LIST (40%) */}
      <aside className="flex h-full flex-1 flex-col md:w-2/5">
        {/* Hero */}
        <div className="border-b border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → last-minute openings
          </p>
          <h1 className="mt-2 text-4xl leading-[1.02] tracking-tight md:text-5xl">
            Book your look,
            <br />
            <span className="text-muted-foreground">last minute.</span>
          </h1>
          <p className="mt-3 max-w-md text-sm text-muted-foreground">
            Cancellations happen. We surface them on the map the second they
            drop — grab a chair, a colourist, or a facial slot right now.
          </p>
        </div>

        {/* Filters */}
        <div className="border-b border-border p-4 md:px-8">
          <div className="mb-2 flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            <Filter size={11} /> Filter
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SERVICE_FILTERS.map((s) => (
              <button
                key={s}
                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}-btn`}
                onClick={() => setFilter(s)}
                className={`border px-2.5 py-1 text-[11px] uppercase tracking-widest transition-colors ${
                  filter === s
                    ? "border-foreground bg-foreground text-background"
                    : "border-border hover:border-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* QR-scan follow banner — only when ?biz=<id> resolved. */}
        {scannedBiz && (
          <ScannedBusinessBanner
            business={scannedBiz}
            onDismiss={() => setScannedBiz(null)}
          />
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 space-y-3" data-testid="landing-openings-loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="skeleton-shimmer h-24 rounded-sm" />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-8 mono text-xs text-muted-foreground">
              No openings match this filter.
            </div>
          )}
          {!loading &&
            filtered.length > 0 &&
            filtered.map((o) => (
              <button
                key={o.id}
                data-testid={`opening-card-${o.id}`}
                onClick={() => openBooking(o)}
                onMouseEnter={() => setSelected(o)}
                className={`hover-lift block w-full border-b border-border p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A85F] focus-visible:ring-inset md:px-8 ${
                  scannedBizId && o.business_id === scannedBizId
                    ? "bg-[#C9A85F]/5 border-l-4 border-l-[#C9A85F]"
                    : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  {o.image_ids && o.image_ids[0] && (
                    <img
                      src={`/api/files/${o.image_ids[0]}`}
                      alt=""
                      aria-hidden="true"
                      className="h-16 w-16 flex-shrink-0 border border-border object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="relative min-w-0 flex-1">
                    {o.is_platinum && (
                      <span
                        data-testid={`platinum-crown-${o.id}`}
                        aria-label="Platinum business"
                        title="Platinum business"
                        className="absolute -top-0.5 right-0 gold-metallic-bg grid h-6 w-6 place-items-center rounded-sm"
                      >
                        <Crown size={12} className="text-[#2A2724]" strokeWidth={2.5} />
                      </span>
                    )}
                    <div className="flex items-center justify-between gap-3 pr-8">
                      <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
                        {o.service_type}
                      </p>
                      <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {minutesUntil(o.start_time)}
                      </p>
                    </div>
                    <h3 className="mt-1.5 text-lg leading-tight tracking-tight">
                      {o.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-muted-foreground truncate">
                      {o.business_name} · {o.address}
                    </p>
                    {(() => {
                      const s = todayStatus(o.business_hours);
                      if (!s.label) return null;
                      const cls =
                        s.tone === "open"
                          ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#059669]"
                          : s.tone === "appt"
                            ? "border-[#C9A85F]/60 bg-[#C9A85F]/10 text-[#8A7333]"
                            : "border-border bg-muted text-muted-foreground";
                      return (
                        <span
                          data-testid={`opening-hours-status-${o.id}`}
                          className={`mono mt-2 inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}
                        >
                          <Clock size={9} />
                          {s.label}
                        </span>
                      );
                    })()}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="mono text-xs">
                        {displayPrice(o.price)}
                      </span>
                      <span className="mono text-[10px] uppercase tracking-widest text-[#C9A85F]">
                        Request →
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
        </div>
      </aside>

      <BookingModal
        opening={selected}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmitted={load}
      />
    </div>
  );
}
